import React, { createContext, useContext, useCallback } from 'react';

const LanguageContext = createContext(null);

const translations = {
  // Nav
  diary: 'Diary',
  drawings: 'Drawings',
  awards: 'Awards',
  logout: 'Logout',
  hi: 'Hi',
  // Settings
  settings: 'Settings',
  appearance: 'Appearance',
  lightMode: 'Light',
  darkMode: 'Dark',
  language: 'Language',
  colorTheme: 'Color Theme',
  // Dashboard
  calendar: 'Calendar',
  markDay: 'Mark Day',
  upcoming: 'Upcoming',
  pickTheme: "Pick today's theme",
  howFeeling: 'How are you feeling?',
  writeAboutDay: 'Write about your day...',
  saveMyDay: 'Save My Day',
  happy: 'Happy',
  calm: 'Calm',
  sad: 'Sad',
  angry: 'Angry',
  tired: 'Tired',
  grassland: 'Grassland',
  activeNow: 'active now',
  dayStreak: 'day streak',
  startStreak: 'Start your streak!',
  // Important dates dialog
  markImportantDay: 'Mark an Important Day',
  markImportantDayDesc: 'Add a star to remember this special day!',
  whatsSpecial: "What's special about this day?",
  pickStarColour: 'Pick a star colour',
  saveImportantDay: 'Save Important Day',
  // WYR
  wyrTitle: 'Would You Rather...',
  wyrSubtitle: 'Tap your choice!',
  wyrQuestion: 'Question',
  wyrOf: 'of',
  wyrOr: 'or',
  wyrNext: 'Next Question',
  wyrDoneTitle: 'All done for today!',
  wyrDoneMsg: 'Come back tomorrow for 30 fresh new questions',
  // Drawings
  myDrawings: 'My Drawings',
  newDrawing: 'New Drawing',
  noDrawings: 'No drawings yet!',
  createFirst: 'Create your first masterpiece',
  drawingTitle: 'Drawing title...',
  save: 'Save',
  saving: 'Saving...',
  tools: 'Tools',
  colours: 'Colours',
  bgColour: 'Background',
  orientation: 'Orientation',
  portrait: 'Portrait',
  landscape: 'Landscape',
  // Awards
  awardsHeading: 'Awards',
  collected: 'collected',
  earnedLabel: 'Earned',
  notYetEarned: 'Not yet earned',
  newAwardEarned: 'New award earned!',
  // Auth
  welcomeBack: 'Welcome back!',
  startAdventure: 'Start your diary adventure!',
  email: 'Email',
  password: 'Password',
  name: 'Name',
  login: 'Login',
  register: 'Register',
  loggingIn: 'Logging in...',
  creating: 'Creating...',
  noAccount: "Don't have an account?",
  haveAccount: 'Already have an account?',
  signUp: 'Sign up',
  signIn: 'Sign in',
  // Prompts
  prompts: [
    "What made you smile today?",
    "Describe your perfect cozy afternoon...",
    "If today were a colour, what would it be and why?",
    "Write about a small moment that felt special...",
    "What song fits your mood right now?",
    "If you could relive one part of today, which would it be?",
    "What are three things you're grateful for today?",
    "Describe something beautiful you noticed today...",
    "What would you tell your future self about today?",
    "If today had a scent, what would it smell like?",
    "Write about someone who made your day better...",
    "What's one thing you learned today?",
    "Describe your happiest moment this week...",
    "If you could paint today, what colours would you use?",
    "What does your ideal tomorrow look like?",
    "Write about a place that makes you feel calm...",
    "What's something kind you did or saw today?",
    "If today were a season, which one would it be?",
    "Describe the view from your favourite spot...",
    "What are you looking forward to?",
    "Write about a memory that made you feel warm inside...",
    "If you could have any superpower just for today, what would it be?",
    "What's a dream you had recently?",
    "Describe your comfort food and why it makes you happy...",
    "Write a tiny letter to someone you care about...",
    "What made today different from yesterday?",
    "If your mood were weather, what would the forecast be?",
    "What's the cosiest thing about your day?",
    "Describe a sound that makes you feel peaceful...",
    "What would your perfect weekend look like?",
  ],
};

export function LanguageProvider({ children }) {
  const t = useCallback((key) => {
    return translations[key] || key;
  }, []);

  const getPrompt = useCallback(() => {
    const prompts = translations.prompts;
    const dayOfYear = Math.floor((new Date() - new Date(new Date().getFullYear(), 0, 0)) / 86400000);
    return prompts[dayOfYear % prompts.length];
  }, []);

  return (
    <LanguageContext.Provider value={{ lang: 'en', setLang: () => {}, t, getPrompt, LANGUAGES: [{ code: 'en', label: 'English' }] }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}
